import { useState } from 'react';

interface LoyaltyBrandMarkProps {
    /** The brand's loyalty mark. Null when they have not uploaded one. */
    logoUrl: string | null;
    /** Shown when there is no mark, and as the accessible name when there is. */
    programName: string;
}

/**
 * The loyalty programme's own mark, or its name when there is no mark.
 *
 * A loyalty programme is usually branded separately from the restaurant — often it *is* the brand a guest
 * recognises — so this is a different image from the restaurant logo on the check screen.
 *
 * Falls back to the name if the image fails to load, not only if the URL is absent. A guest on restaurant
 * wifi is exactly the person whose image request times out, and a broken-image icon where a programme's
 * identity should be is worse than its name in type.
 */
export function LoyaltyBrandMark({ logoUrl, programName }: LoyaltyBrandMarkProps) {
    const [failed, setFailed] = useState(false);

    if (!logoUrl || failed) {
        return (
            <h1 className="text-2xl font-semibold tracking-tight text-balance">{programName}</h1>
        );
    }

    return (
        <h1>
            <img
                src={logoUrl}
                // The programme's name, because that is what the image says. A screen reader user gets the
                // same heading either way.
                alt={programName}
                onError={() => setFailed(true)}
                className="mx-auto max-h-16 w-auto max-w-[12rem] object-contain"
            />
        </h1>
    );
}
